import { Box, Divider } from '@material-ui/core';
import { OnboardingDetailsRole, PaymentMethodType } from '@nemuru-eng/js-api-client';
import { useMemo } from 'react';

import { CustomerSummary } from '@/components/modules/_shared/CustomerSummary/CustomerSummary';
import { useCheckoutSessionContext } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/contexts/CheckoutSessionContext';
import {
  CheckoutSessionDetailEntity,
  Customer,
} from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionDetailEntity';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { BillingAndShippingCard } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/Summary/Customer/BillingAndShipping/BillingAndShippingCard';
import { CustomPaper } from '@/components/shared/components/CustomPaper';

export const CheckoutSessionCustomer = () => {
  const {
    checkoutSessionEntity,
    paymentMethodDetailEntities,
    selectedPaymentMethodType: selectedPaymentMethodType,
  } = useCheckoutSessionContext();

  if (
    !checkoutSessionEntity ||
    (!checkoutSessionEntity.customer && !checkoutSessionEntity.shipping && !checkoutSessionEntity.billing)
  ) {
    return null;
  }

  return (
    <CheckoutSessionCustomerCard
      checkoutSessionEntity={checkoutSessionEntity}
      paymentMethodDetailEntities={paymentMethodDetailEntities}
      selectedPaymentMethodType={selectedPaymentMethodType}
    />
  );
};

interface CheckoutSessionCustomerCardProps {
  checkoutSessionEntity: CheckoutSessionDetailEntity;
  paymentMethodDetailEntities: PaymentMethodDetailEntity[];
  selectedPaymentMethodType?: PaymentMethodType;
}

const CheckoutSessionCustomerCard = ({
  checkoutSessionEntity,
  paymentMethodDetailEntities,
  selectedPaymentMethodType,
}: CheckoutSessionCustomerCardProps) => {
  const divider = checkoutSessionEntity.customer && (checkoutSessionEntity.billing || checkoutSessionEntity.shipping);

  const coBorrowerCustomer = useMemo(
    () => getCoBorrowerCustomer(paymentMethodDetailEntities, selectedPaymentMethodType),
    [paymentMethodDetailEntities, selectedPaymentMethodType],
  );

  const guarantorCustomer = useMemo(
    () => getGuarantorCustomer(paymentMethodDetailEntities, selectedPaymentMethodType),
    [paymentMethodDetailEntities, selectedPaymentMethodType],
  );

  return (
    <CustomPaper fill="paper">
      <CustomerSummary customer={checkoutSessionEntity.customer} />
      {coBorrowerCustomer && (
        <>
          <Box py={2}>
            <Divider light />
          </Box>
          <CustomerSummary customer={coBorrowerCustomer} role={OnboardingDetailsRole.CO_BORROWER} />
        </>
      )}
      {guarantorCustomer && (
        <>
          <Box py={2}>
            <Divider light />
          </Box>
          <CustomerSummary customer={guarantorCustomer} role={OnboardingDetailsRole.GUARANTOR} />
        </>
      )}
      {divider && (
        <Box py={2}>
          <Divider light />
        </Box>
      )}
      <BillingAndShippingCard billing={checkoutSessionEntity.billing} shipping={checkoutSessionEntity.shipping} />
    </CustomPaper>
  );
};

const getCoBorrowerCustomer = (
  paymentMethodDetailEntities: PaymentMethodDetailEntity[],
  selectedPaymentMethodType: PaymentMethodType | undefined,
): Customer | null => {
  const paymentMethodDetailEntity = paymentMethodDetailEntities.find(
    (entity) => entity.type === selectedPaymentMethodType,
  );

  if (!paymentMethodDetailEntity) {
    return null;
  }

  if (paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity === false) {
    return null;
  }

  const coBorrower = (paymentMethodDetailEntity as LoanPaymentMethodDetailEntity).coBorrowerOnboardingDetails();

  if (!coBorrower) {
    return null;
  }

  return {
    documentNumber: coBorrower.invitation.documentNumber ?? coBorrower.userDetails?.documentId.documentNumber ?? null,
    fullName: {
      firstName: coBorrower.invitation.fullName.firstName,
      lastName: coBorrower.invitation.fullName.lastName,
    },
    email: coBorrower.invitation.email,
    phoneNumber: coBorrower.invitation.phoneNumber,
  };
};

const getGuarantorCustomer = (
  paymentMethodDetailEntities: PaymentMethodDetailEntity[],
  selectedPaymentMethodType: PaymentMethodType | undefined,
): Customer | null => {
  const paymentMethodDetailEntity = paymentMethodDetailEntities.find(
    (entity) => entity.type === selectedPaymentMethodType,
  );

  if (!paymentMethodDetailEntity) {
    return null;
  }

  if (paymentMethodDetailEntity instanceof LoanPaymentMethodDetailEntity === false) {
    return null;
  }

  const guarantor = (paymentMethodDetailEntity as LoanPaymentMethodDetailEntity).guarantorOnboardingDetails();

  if (!guarantor) {
    return null;
  }

  return {
    documentNumber: guarantor.invitation.documentNumber ?? guarantor.userDetails?.documentId.documentNumber ?? null,
    fullName: {
      firstName: guarantor.invitation.fullName.firstName,
      lastName: guarantor.invitation.fullName.lastName,
    },
    email: guarantor.invitation.email,
    phoneNumber: guarantor.invitation.phoneNumber,
  };
};
